// =============================================================================
// HoursReportBuilderScreen — /reports/hours/new + /reports/hours/:reportId
// T-370 + T-457/T-458.
// Pick a CLIENT + MONTH, load hours worked on that client's sites, grouped by
// ASSET OWNER or CARRIER. For every person you get ACTUAL (worked) days+hours,
// and an editable REPORTABLE days+hours you send the client. Groups roll up from
// the people. Internal Excel = actual vs reportable; client PDF = clean reportable
// days + hours. Sent reports open read-only.
//
// Rules (confirmed with James 2026-08-04):
//   • On-site day = a day with hours worked OR a travel/LAFHA night.
//   • Includes SUBMITTED + approved timesheet entries (not approved-only).
//   • Reportable defaults to actual days × standard hrs/day; edit per person.
// =============================================================================

import { Fragment, useMemo, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Clock, Save, FileSpreadsheet, FileText, Send, Wand2, AlertTriangle, ChevronDown, ChevronRight,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, KPI, Button, Select, Input, Spinner, EmptyState } from "@/components/ui";
import { exportClientHoursXlsx, downloadClientHoursPdf, type HoursReportExport } from "./clientHoursReportExport";

function workerType(t: string | null | undefined): string {
  if (t === "director") return "Directors";
  if (t === "subcontractor") return "Subbies";
  if (t === "labour_hire") return "Labour hire";
  return "Field";
}
const n2 = (n: number) => Math.round(Number(n) * 100) / 100;
function thisMonth() { return new Date().toISOString().slice(0, 7); }
function monthBounds(month: string) {
  const [y, m] = month.split("-").map(Number);
  const end = new Date(y, m, 0);
  return {
    start: `${month}-01`,
    endISO: `${month}-${String(end.getDate()).padStart(2, "0")}`,
    label: new Date(y, m - 1, 1).toLocaleString("en-AU", { month: "long", year: "numeric" }),
  };
}

type GroupBy = "asset_owner" | "carrier";

interface DetailRow {
  _id: number;
  project_code: string; project_name: string; employee_name: string; worker_type: string;
  asset_owner_name: string; carrier_code: string;
  actual_days: number; actual_hours: number;
  reported_days: number; reported_hours: number;
}
interface GroupRow {
  group_key: string; group_name: string;
  actual_days: number; reported_days: number;
  actual_hours: number; reported_hours: number; note: string;
}

function groupKeyOf(d: DetailRow, groupBy: GroupBy): string {
  return (groupBy === "asset_owner" ? d.asset_owner_name : d.carrier_code) || "—";
}
function computeGroups(detail: DetailRow[], groupBy: GroupBy, notes: Record<string, string>): GroupRow[] {
  const map = new Map<string, GroupRow>();
  for (const d of detail) {
    const key = groupKeyOf(d, groupBy);
    let g = map.get(key);
    if (!g) {
      g = { group_key: key, group_name: key, actual_days: 0, reported_days: 0, actual_hours: 0, reported_hours: 0, note: notes[key] ?? "" };
      map.set(key, g);
    }
    g.actual_days += d.actual_days;
    g.reported_days += d.reported_days;
    g.actual_hours += d.actual_hours;
    g.reported_hours += d.reported_hours;
  }
  return Array.from(map.values())
    .map((g) => ({ ...g, actual_hours: n2(g.actual_hours), reported_hours: n2(g.reported_hours) }))
    .sort((a, b) => b.actual_hours - a.actual_hours);
}

export default function HoursReportBuilderScreen() {
  const user = useRequireAuth();
  const canView = hasRole(user, "manager");
  const { reportId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const isExisting = !!reportId;

  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [month, setMonth] = useState(sp.get("month") ?? thisMonth());
  const [groupBy, setGroupBy] = useState<GroupBy>("asset_owner");
  const [detail, setDetail] = useState<DetailRow[] | null>(null);
  const [groupNotes, setGroupNotes] = useState<Record<string, string>>({});
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<"draft" | "sent">("draft");
  const [flatHrs, setFlatHrs] = useState("10");
  const [busy, setBusy] = useState<string | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const bounds = monthBounds(month);
  const readOnly = status === "sent";
  const groupLabel = groupBy === "asset_owner" ? "Asset owner" : "Carrier";

  const groups = useMemo(
    () => (detail ? computeGroups(detail, groupBy, groupNotes) : []),
    [detail, groupBy, groupNotes],
  );

  // Clients for the picker.
  const { data: clients = [] } = useQuery({
    queryKey: ["hours_report_clients"],
    enabled: !!user && canView && !isExisting,
    queryFn: async () => {
      const { data } = await supabase.from("clients").select("client_id, legal_name").eq("is_active", true).order("legal_name");
      return (data ?? []) as any[];
    },
  });

  // Existing report load.
  const { isLoading: loadingExisting } = useQuery({
    queryKey: ["hours_report", reportId],
    enabled: isExisting,
    queryFn: async () => {
      const { data: hdr, error } = await supabase.from("hours_reports").select("*").eq("report_id", reportId!).maybeSingle();
      if (error) throw error;
      if (!hdr) throw new Error("Report not found");
      const [{ data: gs }, { data: ds }, { data: cl }] = await Promise.all([
        supabase.from("hours_report_groups").select("*").eq("report_id", reportId!).order("sort_order"),
        supabase.from("hours_report_detail").select("*").eq("report_id", reportId!).order("sort_order"),
        supabase.from("clients").select("legal_name").eq("client_id", hdr.client_id).maybeSingle(),
      ]);
      setClientId(hdr.client_id);
      setClientName((cl as any)?.legal_name ?? "");
      setMonth(hdr.period_start.slice(0, 7));
      setGroupBy(hdr.group_by);
      setTitle(hdr.title ?? ""); setNotes(hdr.notes ?? ""); setStatus(hdr.status);
      const noteMap: Record<string, string> = {};
      for (const g of (gs ?? []) as any[]) noteMap[g.group_key] = g.note ?? "";
      setGroupNotes(noteMap);
      setDetail((ds ?? []).map((d: any, i: number) => ({
        _id: i,
        project_code: d.project_code, project_name: d.project_name, employee_name: d.employee_name,
        worker_type: d.worker_type,
        asset_owner_name: d.group_key, carrier_code: d.group_key,
        actual_days: d.on_site_days, actual_hours: Number(d.actual_hours),
        reported_days: d.reported_days ?? d.on_site_days,
        reported_hours: Number(d.reported_hours ?? d.actual_hours),
      })));
      return hdr;
    },
  });

  async function loadActuals(keepEdits = false) {
    if (!clientId) { alert("Pick a client first."); return; }
    setBusy("load");
    const { data: projs, error: pe } = await supabase.from("projects")
      .select(`project_id, project_code, project_name,
        carrier:carrier_id ( carrier_code ),
        site:site_id ( asset_owner:asset_owner_id ( display_name ) )`)
      .eq("client_id", clientId).limit(1000);
    if (pe) { setBusy(null); alert(pe.message); return; }
    const byId = new Map<string, any>();
    for (const p of (projs ?? []) as any[]) byId.set(p.project_id, p);
    const ids = Array.from(byId.keys());
    if (ids.length === 0) { setBusy(null); setDetail([]); setGroupNotes({}); alert("This client has no projects."); return; }

    // Include SUBMITTED + approved (not approved-only).
    const { data: ents, error: ee } = await supabase.from("timesheet_entries")
      .select(`work_date, total_hours, lafha_nights, travel_km, travel_hours, submitted_at, approved_at,
        employee_id, project_id, employees:employee_id ( first_name, last_name, employment_type )`)
      .in("project_id", ids)
      .or("submitted_at.not.is.null,approved_at.not.is.null")
      .gte("work_date", bounds.start).lte("work_date", bounds.endISO).limit(20000);
    setBusy(null);
    if (ee) { alert(ee.message); return; }

    const rate = parseFloat(flatHrs) || 10;
    const map = new Map<string, DetailRow & { _days: Set<string> }>();
    for (const r of (ents ?? []) as any[]) {
      const p = byId.get(r.project_id);
      if (!p) continue;
      const key = `${r.project_id}|${r.employee_id}`;
      const name = [r.employees?.first_name, r.employees?.last_name].filter(Boolean).join(" ") || "Unnamed";
      let g = map.get(key);
      if (!g) {
        g = {
          _id: 0,
          project_code: p.project_code ?? "—", project_name: p.project_name ?? "",
          employee_name: name, worker_type: workerType(r.employees?.employment_type),
          asset_owner_name: p.site?.asset_owner?.display_name ?? "—",
          carrier_code: p.carrier?.carrier_code ?? "—",
          actual_days: 0, actual_hours: 0, reported_days: 0, reported_hours: 0,
          _days: new Set(),
        } as any;
        map.set(key, g);
      }
      g.actual_hours += Number(r.total_hours) || 0;
      // On-site day = hours worked OR a travel/LAFHA night.
      const onSite = (Number(r.total_hours) || 0) > 0
        || (Number(r.lafha_nights) || 0) > 0
        || (Number(r.travel_km) || 0) > 0
        || (Number(r.travel_hours) || 0) > 0;
      if (onSite) g._days.add(r.work_date);
    }
    const rows: DetailRow[] = Array.from(map.values()).map(({ _days, ...d }, i) => {
      const days = _days.size;
      return {
        ...d, _id: i,
        actual_days: days, actual_hours: n2(d.actual_hours),
        reported_days: days, reported_hours: n2(days * rate),
      };
    });
    if (rows.length === 0) alert("No submitted or approved hours for that client + month.");
    setGroupNotes({});
    // 4 Nov 2026 — keep the reported days / hours someone already typed, matched by project + person (used when a saved draft is regrouped)
    const prev = new Map((detail ?? []).map(d => [`${d.project_code}|${d.employee_name}`, d]));
    setDetail(rows.map(r => { const o = prev.get(`${r.project_code}|${r.employee_name}`); return o && keepEdits ? { ...r, reported_days: o.reported_days, reported_hours: o.reported_hours } : r; }));
    setOpenGroup(null);
  }

  function editDetail(id: number, patch: Partial<Pick<DetailRow, "reported_days" | "reported_hours">>) {
    setDetail((prev) => (prev ? prev.map((d) => (d._id === id ? { ...d, ...patch } : d)) : prev));
  }
  function applyFlat() {
    const n = parseFloat(flatHrs);
    if (isNaN(n)) return;
    setDetail((prev) => (prev ? prev.map((d) => ({ ...d, reported_hours: n2(d.reported_days * n) })) : prev));
  }
  function setNote(key: string, v: string) {
    setGroupNotes((prev) => ({ ...prev, [key]: v }));
  }

  const totals = useMemo(() => {
    const ah = groups.reduce((s, g) => s + g.actual_hours, 0);
    const rh = groups.reduce((s, g) => s + g.reported_hours, 0);
    const rd = groups.reduce((s, g) => s + g.reported_days, 0);
    return { actualHrs: n2(ah), reportedHrs: n2(rh), reportedDays: rd, variance: n2(rh - ah) };
  }, [groups]);

  const detailByGroup = useMemo(() => {
    const m = new Map<string, DetailRow[]>();
    for (const d of (detail ?? [])) {
      const key = groupKeyOf(d, groupBy);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(d);
    }
    return m;
  }, [detail, groupBy]);

  async function save(markSent: boolean) {
    if (groups.length === 0) { alert("Load hours first."); return; }
    setBusy(markSent ? "send" : "save");
    const uid = (user as any)?.user_id ?? null;
    const header = {
      client_id: clientId, period_start: bounds.start, period_end: bounds.endISO, group_by: groupBy,
      title: title.trim() || null, notes: notes.trim() || null,
      actual_hours_total: totals.actualHrs, reported_hours_total: totals.reportedHrs,
      status: markSent ? "sent" : "draft", updated_at: new Date().toISOString(), updated_by: uid,
      ...(markSent ? { sent_at: new Date().toISOString() } : {}),
    };
    let rid = reportId;
    if (isExisting) {
      const { error } = await supabase.from("hours_reports").update(header).eq("report_id", reportId!);
      if (error) { setBusy(null); alert(error.message); return; }
      await supabase.from("hours_report_groups").delete().eq("report_id", reportId!);
      await supabase.from("hours_report_detail").delete().eq("report_id", reportId!);
    } else {
      const { data, error } = await supabase.from("hours_reports").insert({ ...header, created_by: uid }).select("report_id").single();
      if (error) { setBusy(null); alert(error.message); return; }
      rid = data.report_id;
    }
    const groupRows = groups.map((g, i) => ({
      report_id: rid, group_key: g.group_key, group_name: g.group_name, person_days: g.reported_days,
      actual_hours: g.actual_hours, reported_hours: g.reported_hours, note: g.note.trim() || null, sort_order: i,
    }));
    const detailRows = (detail ?? []).map((d, i) => ({
      report_id: rid, group_key: groupKeyOf(d, groupBy),
      project_code: d.project_code, project_name: d.project_name, employee_name: d.employee_name,
      worker_type: d.worker_type, on_site_days: d.actual_days, actual_hours: d.actual_hours,
      reported_days: d.reported_days, reported_hours: d.reported_hours, sort_order: i,
    }));
    const { error: ge } = await supabase.from("hours_report_groups").insert(groupRows);
    const { error: de } = await supabase.from("hours_report_detail").insert(detailRows);
    setBusy(null);
    if (ge || de) { alert((ge || de)!.message); return; }
    if (markSent) setStatus("sent");
    if (!isExisting && rid) navigate(`/reports/hours/${rid}`, { replace: true });
    else alert(markSent ? "Marked sent + saved." : "Saved.");
  }

  function exportData(): HoursReportExport {
    return {
      clientName: clientName || "Client",
      periodLabel: bounds.label, periodStart: bounds.start, periodEnd: bounds.endISO,
      groupByLabel: groupLabel,
      groups: groups.map((g) => ({
        group_name: g.group_name, actual_days: g.actual_days, reported_days: g.reported_days,
        actual_hours: g.actual_hours, reported_hours: g.reported_hours, note: g.note,
      })),
      detail: (detail ?? []).map((d) => ({
        group_name: groupKeyOf(d, groupBy), project_code: d.project_code, project_name: d.project_name,
        employee_name: d.employee_name, actual_days: d.actual_days, reported_days: d.reported_days,
        actual_hours: d.actual_hours, reported_hours: d.reported_hours,
      })),
      notes: notes.trim() || null,
    };
  }

  if (!user) return null;
  if (!canView) return <div className="p-6 max-w-2xl mx-auto"><Card><EmptyState icon={AlertTriangle} title="Managers only" description="Client hours reports are available to managers and directors." /></Card></div>;

  const numInput = "w-20 px-2 py-1 rounded border border-line bg-ink-card text-text font-mono text-[13px] text-right";

  return (
    <div className="p-4 md:p-6 max-w-[1150px] mx-auto pb-20">
      <Link to="/reports/hours" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14} /> Client hours reports
      </Link>

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Clock size={20} className="text-accent" /> {isExisting ? "Client hours report" : "New client hours report"}
            {readOnly && <span className="text-[11px] px-2 py-0.5 rounded-full bg-ok/15 text-ok">Sent</span>}
          </h1>
          <p className="text-text-muted text-[13px] mt-1">
            Hours on a client's sites in a month. Actual worked vs reportable — edit each person's days + hours, then export. Includes submitted + approved.
          </p>
        </div>
        {groups.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="secondary" icon={FileSpreadsheet} onClick={() => exportClientHoursXlsx(exportData())} title="Internal — actual vs reportable, days + hours. Not for the client.">Internal Excel</Button>
            <Button variant="secondary" icon={FileText} onClick={() => downloadClientHoursPdf(exportData())} title="Client-facing PDF — reportable days + hours">Client PDF</Button>
            {!readOnly && <Button variant="secondary" icon={Save} disabled={!!busy} onClick={() => save(false)}>{busy === "save" ? "Saving…" : "Save"}</Button>}
            {!readOnly && <Button icon={Send} disabled={!!busy} onClick={() => save(true)}>{busy === "send" ? "Sending…" : "Save & send"}</Button>}
          </div>
        )}
      </div>

      {!isExisting && (
        <Card className="mb-5">
          <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <label className="block md:col-span-2">
              <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Client</span>
              <Select value={clientId} onChange={(e) => { setClientId(e.target.value); setClientName(clients.find((c) => c.client_id === e.target.value)?.legal_name ?? ""); setDetail(null); setGroupNotes({}); }} className="mt-1">
                <option value="">— pick a client —</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.legal_name}</option>)}
              </Select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Month</span>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-full px-3 py-2 rounded border border-line bg-ink-card text-text font-mono text-[13px]" />
            </label>
            <div>
              <Button variant="secondary" disabled={!clientId || busy === "load"} onClick={() => loadActuals()}>{busy === "load" ? "Loading…" : "Load hours"}</Button>
            </div>
          </div>
        </Card>
      )}

      {(detail && detail.length > 0) && (
        <div className="mb-4 inline-flex border border-line rounded-md bg-ink-raised p-0.5 gap-0.5">
          {(["asset_owner", "carrier"] as const).map((g) => (
            <button key={g} type="button" disabled={readOnly && groupBy !== g}
              // 4 Nov 2026 — James: "button not working, won't let me switch." A saved draft only stores one grouping, so switching
              // reloads the hours from the timesheets under the new grouping and keeps the reported figures already typed. A sent report stays as sent.
              onClick={async () => {
                if (groupBy === g || readOnly) return;
                if (isExisting) { if (!window.confirm(`Regroup this draft by ${g === "asset_owner" ? "asset owner" : "carrier"}? Hours reload from the timesheets; the reported days / hours you've typed are kept per person.`)) return; setGroupBy(g); await loadActuals(true); return; }
                setGroupBy(g);
              }}
              className={`px-3 py-1.5 rounded text-[12px] font-medium ${groupBy === g ? "bg-accent text-white" : "text-text-muted hover:text-text"} ${readOnly ? "cursor-default" : ""}`}
              title={readOnly ? "Sent — the grouping is part of the record" : isExisting ? "Switch: hours reload from the timesheets under this grouping" : undefined}>
              {g === "asset_owner" ? "By asset owner" : "By carrier"}
            </button>
          ))}
        </div>
      )}

      {loadingExisting ? (
        <div className="py-16 flex justify-center"><Spinner size={20} /></div>
      ) : !detail ? (
        <Card><EmptyState icon={Clock} title="No hours yet" description="Pick a client and month, then Load hours." /></Card>
      ) : groups.length === 0 ? (
        <Card><EmptyState icon={Clock} title="No hours" description="No submitted or approved timesheet hours for that client + month." /></Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <KPI label="Actual hours" value={totals.actualHrs} />
            <KPI label="Reported hours" value={totals.reportedHrs} accent />
            <KPI label="Reported days" value={totals.reportedDays} />
            <KPI label="Variance (hrs)" value={(totals.variance > 0 ? "+" : "") + totals.variance} accent={Math.abs(totals.variance) > 0} />
          </div>

          {!readOnly && (
            <Card className="mb-4">
              <div className="p-3 flex items-center gap-2 flex-wrap">
                <Wand2 size={15} className="text-accent" />
                <span className="text-[13px] text-text">Set every on-site day to</span>
                <input value={flatHrs} onChange={(e) => setFlatHrs(e.target.value)} className="w-16 px-2 py-1 rounded border border-line bg-ink-card text-text font-mono text-[13px] text-right" />
                <span className="text-[13px] text-text-muted">hrs</span>
                <Button size="sm" variant="secondary" onClick={applyFlat}>Apply to everyone</Button>
                <span className="text-[11px] text-text-dim ml-2">Sets each person's reported hrs = their reported days × this. Tweak anyone after.</span>
              </div>
            </Card>
          )}

          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-line">
                    {[groupLabel, "Days", "Actual hrs", "Reported hrs", "Var", "Note", ""].map((h, i) => (
                      <th key={h + i} className={`px-3 py-2.5 text-[10px] font-medium uppercase tracking-wider2 text-text-muted ${i >= 1 && i <= 4 ? "text-right" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const v = n2(g.reported_hours - g.actual_hours);
                    const open = openGroup === g.group_key;
                    const dets = detailByGroup.get(g.group_key) ?? [];
                    return (
                      <Fragment key={g.group_key}>
                        <tr className="border-b border-line">
                          <td className="px-3 py-2 text-text font-medium">{g.group_name}</td>
                          <td className="px-3 py-2 text-right font-mono text-text-muted">{g.reported_days}</td>
                          <td className="px-3 py-2 text-right font-mono text-text-muted">{n2(g.actual_hours)}</td>
                          <td className="px-3 py-2 text-right font-mono text-accent font-semibold">{n2(g.reported_hours)}</td>
                          <td className={`px-3 py-2 text-right font-mono text-[12px] ${v > 0 ? "text-warn" : v < 0 ? "text-danger" : "text-text-dim"}`}>{v > 0 ? "+" : ""}{v || "0"}</td>
                          <td className="px-3 py-2">
                            {readOnly ? <span className="text-[12px] text-text-muted">{g.note}</span> : (
                              <input value={g.note} onChange={(e) => setNote(g.group_key, e.target.value)} placeholder="—"
                                className="w-full min-w-[120px] px-2 py-1 rounded border border-line bg-ink-card text-text text-[12px]" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button type="button" onClick={() => setOpenGroup(open ? null : g.group_key)} className="text-text-muted hover:text-accent" title="Edit people">
                              {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            </button>
                          </td>
                        </tr>
                        {open && (
                          <tr className="bg-ink-raised">
                            <td colSpan={7} className="px-3 py-2.5">
                              <table className="w-full text-[12px]">
                                <thead>
                                  <tr className="text-text-dim">
                                    <th className="text-left font-medium py-1">Project</th>
                                    <th className="text-left font-medium py-1">Employee</th>
                                    <th className="text-right font-medium py-1">Actual</th>
                                    <th className="text-right font-medium py-1">Rep. days</th>
                                    <th className="text-right font-medium py-1">Rep. hrs</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dets.map((d) => (
                                    <tr key={d._id} className="border-t border-line/50">
                                      <td className="py-1.5 font-mono text-accent">{d.project_code}</td>
                                      <td className="py-1.5 text-text">{d.employee_name} <span className="text-text-dim">· {d.worker_type}</span></td>
                                      <td className="py-1.5 text-right font-mono text-text-dim">{d.actual_days}d · {n2(d.actual_hours)}h</td>
                                      <td className="py-1.5 text-right">
                                        {readOnly ? <span className="font-mono">{d.reported_days}</span> : (
                                          <input type="number" value={d.reported_days}
                                            onChange={(e) => editDetail(d._id, { reported_days: parseInt(e.target.value) || 0 })}
                                            className={numInput} />
                                        )}
                                      </td>
                                      <td className="py-1.5 text-right">
                                        {readOnly ? <span className="font-mono">{n2(d.reported_hours)}</span> : (
                                          <input type="number" value={d.reported_hours}
                                            onChange={(e) => editDetail(d._id, { reported_hours: parseFloat(e.target.value) || 0 })}
                                            className={numInput} />
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-line font-mono">
                    <td className="px-3 py-2.5 font-semibold">Total</td>
                    <td className="px-3 py-2.5 text-right text-text-muted">{totals.reportedDays}</td>
                    <td className="px-3 py-2.5 text-right text-text-muted">{totals.actualHrs}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-accent">{totals.reportedHrs}</td>
                    <td className="px-3 py-2.5 text-right">{totals.variance > 0 ? "+" : ""}{totals.variance}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {!readOnly && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Title (optional)</span>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Ventia — June hours" className="mt-1" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider2 text-text-muted">Note for the client (optional)</span>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Shown on the PDF" className="mt-1" />
              </label>
            </div>
          )}
        </>
      )}
    </div>
  );
}
